import { createContext, useContext, useEffect } from "react";
import { invariant } from "@pexip-engage/utils";
import { ActionFunctionArgs, redirect } from "@remix-run/node";
import { unstable_useBlocker, useFetcher, useParams, useSubmit } from "@remix-run/react";
import clsx from "clsx";
import { z } from "zod";

import { Button } from "~/components/Button";
import { useActiveStep } from "~/hooks/useActiveStep";
import { createServerStateManager } from "~/modules/StateManager.server";
import { StepConfig } from "~/utils/getFlow";

const schema = z.enum([
  "customer",
  "employee",
  "meeting-type",
  "office",
  "questions",
  "subject",
  "timetable",
]);

export async function action(args: ActionFunctionArgs) {
  const result = schema.safeParse(args.params.stepId);
  if (!result.success) return null;

  const stateManager = (await createServerStateManager(args)).revertToStep(result.data);
  await stateManager.persist();

  return redirect(`../${result.data}`);
}

interface GoBackFormProps {
  stepId: StepConfig["id"];
  className?: string;
  children: React.ReactNode;
  formClassName?: string;
}

const GoBackContext = createContext<"schedule" | "invite" | null>(null);
export const GoBackProvider = GoBackContext.Provider;

function useGoBackUrl() {
  const ctx = useContext(GoBackContext);
  const { enterprise, sessionId } = useParams<"sessionId" | "enterprise">();

  invariant(ctx, "Add a GoBackProvider");
  const path = ctx === "schedule" ? "schedule" : "appointment/invite";

  return `/${enterprise}/plugin/${sessionId}/${path}/api/go-back`;
}

export function GoBackButton({ formClassName, stepId, ...props }: GoBackFormProps) {
  const fetcher = useFetcher<typeof action>();
  const action = useGoBackUrl();

  return (
    <fetcher.Form action={`${action}/${stepId}`} className={formClassName} method="post">
      <Button
        data-component="StepEditLink"
        type="submit"
        {...props}
        className={clsx(props.className, "text-left")}
      />
    </fetcher.Form>
  );
}

export function useBrowserNavigationInterceptor() {
  const submit = useSubmit();
  const action = useGoBackUrl();
  const prevStepId = useActiveStep().prevStep?.id;
  const blocker = unstable_useBlocker(({ historyAction }) => historyAction === "POP");

  useEffect(() => {
    if (blocker.state === "blocked") {
      blocker.reset();

      if (prevStepId) {
        submit(null, {
          action: `${action}/${prevStepId}`,
          method: "post",
          replace: true,
        });
      } else {
        /**
         * This allows users to use "back" entirely when we've hit our first plugin page. Entries are created when navigating, but because we're stopping default POPs, they dont get removed.
         * This is a workaround to force back the entire stack.
         * history.state.idx is added by react router. We can use this to know how many history stacks we've pushed.
         * The +2 is:
         * - +1 because we've stopped the current pop event, and its counted
         * - +1 to "go" to the history entry right before we've added entries.
         *
         * And then we do *-1 to negate the value to force back the entire stack in one go.
         */
        const idx = window.history.state?.idx;
        if (typeof idx === "number" && !Number.isNaN(idx)) {
          const goTo = (idx + 2) * -1;
          window.history.go(goTo);
        }
      }
    }
  }, [submit, blocker, action, prevStepId]);
}
